import type { Api_LiveStream, Api_Video } from '@core/api'
import { resolveStoreParams } from '@core/params'
import type {
  WidgetInterruptCallbackFn,
  WidgetInterruptEvent,
} from '@core/widgets/interrupts/types'
import type { GetState, NamedSet } from '@core/zustand'
import {
  detectOutdatedFeedLiveStreamStatus,
  recordAndDetectLiveStreamStatus,
  updateVideoLiveStreamParams,
} from '@embed/helpers'
import type { AnyWidgetAppContext } from '@embed/hooks/useEmbedAppContext'

import type { IWidgetState, SubtitlesMode } from './types'

export type IStoreActions = ReturnType<typeof actions>

/**
 *
 */
export const actions = (
  set: NamedSet<IWidgetState>,
  get: GetState<IWidgetState>,
) => ({
  // Common actions between Widgets and fw.tv
  setContext(appContext: AnyWidgetAppContext) {
    // Resets engagement tracking state when the context changes
    set(
      {
        appContext,
        engagement: {},
      },
      false,
      'setContext',
    )
  },
  // Widgets specific actions
  fetchedParams(fetchedRemoteParams: IWidgetState['fetchedParams']) {
    const state = get()
    const { params, fetchedParams } = resolveStoreParams(state, {
      fetchedParams: fetchedRemoteParams,
    })

    if (fetchedParams) {
      set(
        {
          params,
          fetchedParams,
        },
        false,
        'fetchedParams',
      )
    }
  },
  setExperimentParams(experimentParams: IWidgetState['experimentParams']) {
    const state = get()
    const resolved = resolveStoreParams(state, { experimentParams })

    set(
      {
        params: resolved.params,
        experimentParams: resolved.experimentParams ?? {},
      },
      false,
      'setExperimentParams',
    )
  },
  setCohortParams(fromCohortParams: IWidgetState['params']) {
    set(() => ({ fromCohortParams }), false, 'setCohortParams')
  },

  /**
   * Sets fetched videos to the store for later use.
   * @param videos
   */
  setVideos: (videos: Api_Video[]) => {
    videos.forEach(detectOutdatedFeedLiveStreamStatus)
    set(
      {
        videos: videos.reduce((map, video) => {
          map[video.encoded_id] = video
          return map
        }, {}),
        navigation: videos.map((video) => video.encoded_id),
        placeholders: [],
      },
      false,
      'setVideos',
    )
  },

  /**
   * Updates video livestream data from the livestream object
   * received from the WebSocket. WebSocket is used to provide
   * livestream status updates.
   * @param liveStreamId
   * @param liveStream
   */
  updateVideoFromLiveStream: (
    liveStreamId: string,
    liveStream: Api_LiveStream,
  ) => {
    recordAndDetectLiveStreamStatus(
      get().videos[liveStream.video_id],
      liveStream,
    )
    set(
      (state) => ({
        videos: {
          ...state.videos,
          [liveStream.video_id]: {
            ...state.videos[liveStream.video_id],
            ...updateVideoLiveStreamParams(
              state.videos[liveStream.video_id],
              liveStream,
            ),
          },
        },
      }),
      false,
      'updateVideoFromLiveStream',
    )
  },
  /**
   * Toggle mute state for videos in the feed.
   */
  toggleMute: () => {
    set(
      (state) => ({
        muted: !state.muted,
      }),
      false,
      'toggleMute',
    )
  },
  /**
   * Set the engaged video id in the store.
   * @param videoId
   */
  setEngagedVideo: (videoId: string | null) => {
    set(
      {
        engagedVideoId: videoId,
      },
      false,
      'setEngagedVideo',
    )
  },
  setEngagedVideoV2: (videoId: string | null) => {
    set(
      {
        engagedVideoIdV2: videoId,
      },
      false,
      'setEngagedVideoV2',
    )
  },
  setEmbedConfigParams: (
    embedConfigRemoteParams: IWidgetState['embedConfigParams'],
  ) => {
    const state = get()
    const { params, embedConfigParams } = resolveStoreParams(state, {
      embedConfigParams: embedConfigRemoteParams,
    })

    if (embedConfigParams) {
      set(
        {
          params,
          embedConfigParams,
        },
        false,
        'setEmbedConfigParams',
      )
    }
  },

  setSubtitlesEnabled(subtitlesEnabled: boolean) {
    set(
      (state) => ({
        subtitlesEnabled,
        subtitlesLanguage: subtitlesEnabled ? state.subtitlesLanguage : null,
        currentSubtitlesId: subtitlesEnabled ? state.currentSubtitlesId : null,
      }),
      false,
      'setSubtitlesEnabled',
    )
  },

  setSubtitles: (subtitlesLanguage: string, subtitlesId: string) => {
    set(
      {
        subtitlesEnabled: true,
        currentSubtitlesId: subtitlesId,
        subtitlesLanguage,
      },
      false,
      'enableSubtitles',
    )
  },

  setSubtitlesMode: (subtitlesMode: SubtitlesMode) => {
    set(
      {
        subtitlesMode,
      },
      false,
      'setSubtitlesMode',
    )
  },

  setAutoCCMessageShown: () => {
    set(
      {
        autoCCMessageShown: true,
      },
      false,
      'setAutoCCMessageShown',
    )
  },

  /**
   * Register a callback to be called when a certain event occurs.
   * @param event
   * @param fn
   */
  registerInterruptionCallback: (
    event: WidgetInterruptEvent,
    fn: WidgetInterruptCallbackFn,
  ) => {
    set(
      (state) => ({
        interruptionCallbacks: {
          ...state.interruptionCallbacks,
          [event]: fn,
        },
      }),
      false,
      'registerWidgetInterruptionCallback',
    )
  },

  /**
   * Unregister a callback to be called when a certain event occurs.
   * @param event
   */
  unregisterInterruptionCallback: (event: WidgetInterruptEvent) => {
    set(
      (state) => ({
        interruptionCallbacks: {
          ...state.interruptionCallbacks,
          [event]: undefined,
        },
      }),
      false,
      'unregisterWidgetInterruptionCallback',
    )
  },
})
